import { Translate, useTranslation } from "@redotech/redo-i18n";
import { BooleanFilterOperator } from "@redotech/redo-views-common/advanced-filters/boolean-filter";
import { memo } from "react";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
  RedoFilterSection,
} from "../../arbiter-components/filter-group/redo-filter-group";
import { SimpleFilterDropdown } from "../../filters/simple-filter-dropdown";
import { BooleanTableFilter } from "../advanced-filter";

export const BooleanFilterGroup = memo(function BooleanFilterGroup({
  filter,
  setFilter,
  removeFilter,
}: {
  filter: BooleanTableFilter;
  setFilter(filter: BooleanTableFilter): void;
  removeFilter(): void;
  openOnRender: boolean;
}) {
  const { t } = useTranslation();
  const { value, operator } = filter.data;
  const conditionHolds =
    value == null
      ? true
      : operator === BooleanFilterOperator.IS_NOT
        ? !value
        : value;
  const displayedOperator = conditionHolds
    ? BooleanFilterOperator.IS
    : BooleanFilterOperator.IS_NOT;
  const valueText = filter.boolDisplay
    ? filter.boolDisplay.trueDisplay
    : t("true", { context: "boolean filter value" });
  const setOperator = (next: BooleanFilterOperator) => {
    setFilter({
      ...filter,
      data: { ...filter.data, value: true, operator: next },
    });
  };

  return (
    <RedoFilterGroup
      Icon={filter.Icon}
      propertyName={filter.label ? filter.label : filter.displayName}
      query={
        <SimpleFilterDropdown
          filterStyle="query"
          options={filter.operators || Object.values(BooleanFilterOperator)}
          optionToFriendlyName={(option) => getBooleanOperatorLabel(t, option)}
          setValue={setOperator}
          toggle
          value={displayedOperator}
        />
      }
      removeFilter={removeFilter}
      value={
        value == null ? (
          <RedoFilterDropdownAnchor
            color="primary"
            onClick={() => setOperator(displayedOperator)}
            text="..."
            weight="medium"
          />
        ) : (
          <RedoFilterSection text={valueText} weight="medium" />
        )
      }
    />
  );
});

function getBooleanOperatorLabel(
  t: Translate,
  operator: BooleanFilterOperator,
): string {
  switch (operator) {
    case BooleanFilterOperator.IS:
      return t("is", { context: "filter operator" });
    case BooleanFilterOperator.IS_NOT:
      return t("is not", { context: "filter operator" });
  }
}
